import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { GetPatientAuditLogAPI } from '../../../../api/API';

const DEFAULT_PAGINATION = { total: 0, page: 1, limit: 25, pages: 1 };

/**
 * useCompanyAudit
 *
 * Hook responsável pela tela "Auditoria" da clínica (LGPD L-14): lista paginada/filtrada
 * da trilha de acessos/alterações de pacientes (audit_log.php), visível só ao proprietário.
 * Filtros (ação, usuário, período, página) e o paciente (quando vindo do atalho da ficha)
 * vivem na URL via useSearchParams, no mesmo padrão de useAdminAudit, para que o link
 * possa ser compartilhado.
 *
 * @returns {Object} estado e ações da tela
 */
function useCompanyAudit() {
  const [searchParams, setSearchParams] = useSearchParams();

  const updateParams = useCallback((updates, resetPage = true) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') {
          next.delete(key);
        } else {
          next.set(key, String(value));
        }
      });
      if (resetPage) next.delete('page');
      return next;
    }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Filtros (na URL) ───────────────────────────────────────────────────────
  const patientId = searchParams.get('patient') || '';
  const action = searchParams.get('action') || '';
  const userId = searchParams.get('user') || '';
  const dateFrom = searchParams.get('date_from') || '';
  const dateTo = searchParams.get('date_to') || '';
  const page = parseInt(searchParams.get('page'), 10) || 1;

  const setAction = useCallback((value) => updateParams({ action: value }), [updateParams]);
  const setUserId = useCallback((value) => updateParams({ user: value }), [updateParams]);
  const setDateFrom = useCallback((value) => updateParams({ date_from: value }), [updateParams]);
  const setDateTo = useCallback((value) => updateParams({ date_to: value }), [updateParams]);
  const setPage = useCallback((value) => updateParams({ page: value }, false), [updateParams]);
  const clearPatient = useCallback(() => updateParams({ patient: undefined }), [updateParams]);

  // ─── Lista (audit_log.php) ──────────────────────────────────────────────────
  const [entries, setEntries] = useState([]);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [actions, setActions] = useState([]);
  const [users, setUsers] = useState([]);
  const [patient, setPatient] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchEntries = useCallback(() => {
    setIsLoading(true);
    setError(null);
    GetPatientAuditLogAPI(
      { patientId, action, userId, dateFrom, dateTo, page, limit: DEFAULT_PAGINATION.limit },
      (response) => {
        if (!response || response.status !== 'SUCCESS') {
          setError(response?.message || 'Erro ao carregar o histórico de acessos. Tente novamente.');
          setIsLoading(false);
          return;
        }
        const data = response.data || {};
        setEntries(data.entries || []);
        setPagination(data.pagination || { ...DEFAULT_PAGINATION, page });
        setActions(data.actions || []);
        setUsers(data.users || []);
        setPatient(data.patient || null);
        setIsLoading(false);
      }
    );
  }, [patientId, action, userId, dateFrom, dateTo, page]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  return {
    entries,
    pagination,
    actions,
    users,
    patient,
    isLoading,
    error,

    patientId,
    clearPatient,
    action,
    setAction,
    userId,
    setUserId,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    setPage,

    refresh: fetchEntries,
  };
}

export default useCompanyAudit;
